import React from 'react';
import {
  IonButton,
  IonContent,
  IonPage,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import ShareWizardHeader from './ShareWizardHeader';
import RecipientPickerBody from './RecipientPickerBody';
import RecipientsSkeleton from './RecipientsSkeleton';
import { SHARE_STEP_HELP_TEXT } from './ShareContentSkeleton';

/**
 * The wizard's frame around the picker: the page, the wizard header, and the
 * classic "Next (N selected)" that hands off to whichever step follows.
 * Everything about picking people lives in RecipientPickerBody.
 *
 * Some ways into the flow (the welcome sheet's reshare prompt, a shared link)
 * skip the share dock's guard, so the step checks share:topics itself: a sender
 * who can't share yet sees what they still owe in place of the picker.
 */
const RecipientsStep: React.FC = () => {
  const { topic, isTopicLoading, topicError, recipients, nav } =
    useShareWizardContext();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  return (
    <IonPage>
      <ShareWizardHeader
        topicTitle={topic?.title ?? ''}
        title="Recipients"
        progress={nav.progress}
      />
      <ActionGuardContainer>
        <ActionGuard
          disableMode="hidden"
          optimistic
          action={ACTIONS.SHARE}
          resource={RESOURCES.TOPICS}
          resourceId={topic?.id}
          helpText={SHARE_STEP_HELP_TEXT}
          fallbackElem={lock => <RecipientsSkeleton lock={lock} />}
          guardedElem={
            <IonContent
              style={{ '--background': 'var(--app-page-background)' }}
            >
              {isTopicLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <IonSpinner />
                </div>
              ) : topicError ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <p style={{ color: '#c33' }}>{topicError}</p>
                  <IonButton onClick={nav.exit} fill="clear">
                    Close
                  </IonButton>
                </div>
              ) : (
                <RecipientPickerBody
                  ctaLabel={
                    recipients.length > 0
                      ? `Next (${recipients.length} selected)`
                      : 'Next'
                  }
                  onAdvance={nav.goForward}
                  ctaTestId="share-wizard-next"
                />
              )}
            </IonContent>
          }
        />
      </ActionGuardContainer>
    </IonPage>
  );
};

export default RecipientsStep;
